import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  debounce,
  DEFAULT_INPUT_DEBOUNCE_DELAY,
  Logger,
  useAvoidFirstRender,
} from '@saviynt/common';
import {
  Badge,
  ButtonSelect,
  Icon,
  InputField,
  MenuMulti,
  MobileDrawerItem,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  searchFilterValuesByText,
  setIsBulkActionsCompleted,
} from '../../../redux/certdetails/action';
import { useInvertedLocalize } from '../../../utils/localization';

import { MIN_COUNT_TO_SEARCH_IN_FILTER_CHIP } from './constant';
import { invertedMessages } from './messages';

function ChipFilter({
  filter,
  handleRemoveFilter,
  setPersonalizationStatus,
  handleMultiMenuChange,
  certKey,
  countOfCheckedOptions,
  // updateFiltersAfterSearch,
  updateFiltersRecords,
  isFilterOpen,
  isMobileResponsivenessEnabled,
}) {
  const dispatch = useDispatch();

  const l = useInvertedLocalize(invertedMessages);

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [prioritizeCheckedOptions, setPrioritizeCheckedOptions] =
    useState(false);
  const [mobileFilter, setMobileFilter] = useState(filter); // For mobile resposiveness below 600px
  const [filterText, setFilterText] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMoreOptions, setHasMoreOptions] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const menuMultiRef = useRef(null);
  const chipWrapperRef = useRef(null);

  // Close dropdown when focus leaves the chip via Tab key navigation
  useEffect(() => {
    if (!isExpanded) return () => {};

    const wrapper = chipWrapperRef.current;

    if (!wrapper) return () => {};

    const handleFocusOut = (event) => {
      // relatedTarget is the element receiving focus.
      // null means a non-focusable element was clicked — ignore (handled by MenuMulti's outside click).
      if (!event.relatedTarget) return;

      if (!wrapper.contains(event.relatedTarget)) {
        if (menuMultiRef.current) {
          menuMultiRef.current.closeDropdown();
        }
      }
    };

    wrapper.addEventListener('focusout', handleFocusOut);

    return () => wrapper.removeEventListener('focusout', handleFocusOut);
  }, [isExpanded]);

  const LOAD_INCREMENT = 10;

  useEffect(() => {
    setOptions(filter.options);
    setMobileFilter(filter);
  }, [filter]);

  const getFilterList = async (searchText, maxCount, offSet = 0) => {
    const body = {
      certkey: certKey,
      // "searchtext":"ro", // searchtext is optional parameter. to search in filter for available values
      // "searchtext":"ro_ent5_displayname", // searchtext is optional parameter. to search in filter for available values
      fieldtosearch: filter.key, // filter column name where searchtext or more option(data) is fetched
      // moreoptions: 'true', // while fetching more records, below params must be true
      offset: `${offSet}`,
      fieldnameSize: `${maxCount}`, // it will always fetch default 10 records + fieldnameSize
      // moreoptions and fieldnameSize are mandatory together
    };

    if (searchText) {
      // to search filters by name
      body.searchtext = searchText;
    }

    if (!isLoading) {
      setIsLoading(true);
    }

    dispatch(searchFilterValuesByText(body))
      .then((data) => {
        if (data?.valueCountMap) {
          if (
            offSet === 0 &&
            data.valueCountMap[filter.key] &&
            Object.keys(data.valueCountMap[filter.key]).length === 0
          ) {
            setHasMoreOptions(false);
            updateFiltersRecords({}, filter.key, offSet);
          } else if (
            offSet === 0 &&
            data.valueCountMap[filter.key] &&
            Object.keys(data.valueCountMap[filter.key]).length
          ) {
            updateFiltersRecords(
              data.valueCountMap[filter.key],
              filter.key,
              offSet
            );
            setHasMoreOptions(true);
          } else if (
            offSet !== 0 &&
            data.valueCountMap[filter.key] &&
            Object.keys(data.valueCountMap[filter.key]).length === 0
          ) {
            setHasMoreOptions(false);
          } else if (
            offSet !== 0 &&
            data.valueCountMap[filter.key] &&
            Object.keys(data.valueCountMap[filter.key]).length
          ) {
            updateFiltersRecords(
              data.valueCountMap[filter.key],
              filter.key,
              offSet
            );
            setHasMoreOptions(true);
          }
        } else {
          // set empty object
          updateFiltersRecords({}, filter.key, offSet);
        }
      })
      .catch((error) => {
        Logger.error('chip filter search error ', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isExpanded) {
      setOptions([]);
      setOffset(0); // Reset offset on open to ensure scroll pagination starts fresh
      setIsLoading(true);
      setHasMoreOptions(true);
      getFilterList(filterText, LOAD_INCREMENT, 0);
    }
  }, [isExpanded]);

  const loadFilterList = useCallback((searchTermValue, offsetValue = 0) => {
    getFilterList(searchTermValue, LOAD_INCREMENT, offsetValue);
  }, []);

  // Wrap load filter list with debounce
  const debouncedloadFilterList = useCallback(
    debounce(loadFilterList, DEFAULT_INPUT_DEBOUNCE_DELAY),
    [loadFilterList]
  );

  useEffect(() => {
    // if (!filter?.options?.length && filter.initialCount > 0) {
    // debouncedloadFilterList(); // to fetch chip list
    // loadFilterList(searchTerm, offset, LOAD_INCREMENT);
    getFilterList(filterText, LOAD_INCREMENT, offset);
    // }/
  }, []);

  // For the devices below than 600px width
  const [searchTextWhenMobile, setSearchTextWhenMobile] = useState('');

  const handleChangeForMobile = (optionValueKey) => {
    setMobileFilter((prev) => ({
      ...prev,
      options: prev.options.map((option) =>
        option.value === optionValueKey
          ? { ...option, isChecked: !option.isChecked }
          : option
      ),
    }));
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollHeight - scrollTop === clientHeight &&
      hasMoreOptions &&
      !isLoading
    ) {
      // load more options
      setIsLoading(true);
      setOffset((prevOffset) => {
        const newOffset = prevOffset + LOAD_INCREMENT;

        debouncedloadFilterList(filterText, newOffset);

        return newOffset;
      });
    }
  };

  useEffect(() => {
    if (isMobileResponsivenessEnabled) {
      const selectedOptions =
        mobileFilter.options.filter((option) => option.isChecked) || [];

      handleMultiMenuChange(mobileFilter.key, selectedOptions);
      dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
      dispatch(setIsBulkActionsCompleted(false));
    }
  }, [mobileFilter]);

  useAvoidFirstRender(() => {
    if (isMobileResponsivenessEnabled && isFilterOpen) {
      debouncedloadFilterList(searchTextWhenMobile.trim());

      if (searchTextWhenMobile !== '') {
        setPrioritizeCheckedOptions(true);
      }
    }
  }, [searchTextWhenMobile]);

  if (isMobileResponsivenessEnabled) {
    return (
      <>
        {filter.initialCount >= MIN_COUNT_TO_SEARCH_IN_FILTER_CHIP && (
          <div className='AddFilterDropdown-dropDownSearchBar--mobileResponsive'>
            <InputField
              name='basic'
              placeholder='Search'
              kind='search'
              size='large'
              prefixIcon={
                <Icon kind='search' color='neutral-700' size='smallMedium' />
              }
              isDisabled={!isFilterOpen}
              value={searchTextWhenMobile}
              setValue={setSearchTextWhenMobile}
              tabIndex={0}
            />
          </div>
        )}
        <div className='AdvancedFilters-filterDrawerStep2NormalFilterOptions'>
          {mobileFilter?.options
            ?.sort((a, b) => {
              if (searchTextWhenMobile !== '' && prioritizeCheckedOptions)
                return !b.isChecked - !a.isChecked;

              return b.isChecked - a.isChecked;
            })
            ?.map((option) => (
              <MobileDrawerItem
                key={option.value}
                option={{ ...option, isMulti: true }}
                onClick={() => {
                  handleChangeForMobile(option.value);

                  if (prioritizeCheckedOptions) {
                    setPrioritizeCheckedOptions(false);
                  }

                  if (searchTextWhenMobile !== '')
                    debouncedloadFilterList(searchTextWhenMobile.trim());
                }}
              />
            ))}
          {mobileFilter?.options?.length === 0 && (
            <Typography
              kind='body3'
              className='AdvancedFilters-filterDrawerStep2NormalFilter--noDataScreen'>
              {l('No data available')}
            </Typography>
          )}
        </div>
      </>
    );
  }

  return (
    <div ref={chipWrapperRef}>
      <MenuMulti
        ref={menuMultiRef}
        classNames={{
          dropdownClassName: 'AdvancedFilters-dropdownMenu',
        }}
        key={filter.key}
        options={options}
        appendLabelsToButton={false}
        label={filter.label}
        onChange={(selectedOptions) => {
          handleMultiMenuChange(filter.key, selectedOptions);
          dispatch(setIsBulkActionsCompleted(false));

          if (prioritizeCheckedOptions) {
            setPrioritizeCheckedOptions(false);
          }
        }}
        trigger={
          <ButtonSelect
            kind='pill'
            label=''
            BadgeComp={
              countOfCheckedOptions && countOfCheckedOptions > 0 ? (
                <Badge kind='number' number={countOfCheckedOptions} />
              ) : null
            }
          />
        }
        minOptionsForFooter={0}
        minOptionsForSearch={
          filter.initialCount < MIN_COUNT_TO_SEARCH_IN_FILTER_CHIP
            ? options.length + 1
            : 0
        }
        sortOnCheck
        shouldSortCheckedAtBottom={prioritizeCheckedOptions}
        headerText={filter?.appliedTo}
        headerTextConfigs={{
          headerTextClassName: 'AdvancedFilters-filterBarMenuItemHeaderText',
          splitOnToken: true,
          token: ':',
          preTokenClassName:
            'AdvancedFilters-filterBarMenuItemHeaderTextPreToken',
          postTokenClassName:
            'AdvancedFilters-filterBarMenuItemHeaderTextPostToken',
        }}
        footerLink1Text={l('Remove Filter')}
        isFilteringDisabled
        onFooterLink1Click={() => handleRemoveFilter(filter.key)}
        onSearchTermChange={(searchText) => {
          setFilterText(searchText);
          setPrioritizeCheckedOptions(searchText !== '');
          debouncedloadFilterList(searchText.trim());
          setOffset(0);
        }}
        showOptionLabelOnHover
        showFooterLink1
        onScroll={handleScroll}
        hasMoreOptions={hasMoreOptions}
        isLoading={isLoading}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        noDataText={isLoading ? ' ' : l('No data available')}
      />
    </div>
  );
}

ChipFilter.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({})),
    appliedTo: PropTypes.string,
    initialCount: PropTypes.number,
  }),
  handleRemoveFilter: PropTypes.func.isRequired,
  setPersonalizationStatus: PropTypes.func.isRequired,
  handleMultiMenuChange: PropTypes.func.isRequired,
  certKey: PropTypes.string.isRequired,
  countOfCheckedOptions: PropTypes.number.isRequired,
  // updateFiltersAfterSearch: PropTypes.func.isRequired,
  updateFiltersRecords: PropTypes.func.isRequired,
  isFilterOpen: PropTypes.bool,
  isMobileResponsivenessEnabled: PropTypes.bool,
};

ChipFilter.defaultProps = {
  filter: {},
  isFilterOpen: false,
  isMobileResponsivenessEnabled: false,
};

export default ChipFilter;
